import routes from 'config/routes'
import { fetch } from 'utils/fetch'

type FetcherResult =
  | { ok: true; data: Record<string, unknown> }
  | { ok: false; code: string; retryable: boolean; message: string }

// The Gousto backend proxy at `routes.cms.entry` auto-camelCases response keys
// (e.g. `image_with_text` → `imageWithText`, `_metadata` → `Metadata`). The
// @library/cms-blocks runtime keys on the snake_case schema uids, so we revert
// the transformation here. SSR uses fetchByUrl direct-to-ContentStack and is
// unaffected.
function camelToSnake(key: string): string {
  return key.replace(/([A-Z])/g, '_$1').toLowerCase()
}

function toSnakeCaseKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(toSnakeCaseKeys)
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[camelToSnake(key)] = toSnakeCaseKeys(val)
    }

    return out
  }

  return value
}

async function fetchCmsEntry(contentType: string, pageUrl: string): Promise<FetcherResult> {
  try {
    const response = (await fetch(null, routes.cms.entry, { contentType, pageUrl }, 'GET')) as {
      data?: Record<string, unknown>
    } | null

    if (response?.data) {
      return { ok: true, data: toSnakeCaseKeys(response.data) as Record<string, unknown> }
    }

    return {
      ok: false,
      code: 'BAD_RESPONSE',
      retryable: false,
      message: 'CMS response missing data',
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'

    return { ok: false, code: 'NETWORK', retryable: true, message }
  }
}

export { fetchCmsEntry }
